import type { ReactNode } from "react";

import type { Observation } from "../lib/observations";
import { InStockStrip } from "./InStockStrip";
import { Money } from "./Money";
import { type RowState, StateLabel, stateStripeClass } from "./StateLabel";

/**
 * The shelf-edge price label — one store's listing of one product, modelled on
 * the printed labels clipped to shop shelves. One store, one price, one state:
 * a group of listings is a product summary above a set of these, never a widened
 * Row, because a group has a *set* of states and one stripe cannot say that.
 */
export function Row({
	title,
	store,
	state,
	priceMinor,
	stateLabel,
	meta,
	observations = [],
	now,
	href,
	action,
}: {
	title: string;
	store: string;
	state: RowState;
	priceMinor: number | null;
	stateLabel?: string;
	meta?: string;
	observations?: Observation[];
	now?: number;
	href?: string;
	action?: ReactNode;
}) {
	const inner = (
		<>
			<span className={`self-stretch ${stateStripeClass(state)}`} />
			<span className="min-w-0 py-2">
				<span className="block truncate text-[13px] font-medium">{title}</span>
				<span className="mt-0.5 flex items-center gap-2 text-[11px] text-muted">
					<span className="font-medium text-ink/80 capitalize">{store}</span>
					{observations.length > 0 && now !== undefined && (
						// `live` comes from the caller's known state, never from comparing
						// an interval's `to` against wall-clock now: observed_to only
						// advances when a poll confirms the state, so it lags by up to a
						// sweep interval (10 min default) and proximity to now cannot
						// identify the current state.
						<InStockStrip observations={observations} now={now} live={state === "live"} />
					)}
					{meta && <span className="truncate">{meta}</span>}
				</span>
			</span>
			<span className="py-2 pr-3 text-right whitespace-nowrap">
				<Money minor={priceMinor} className="block text-[15px] leading-5" />
				<StateLabel state={state} label={stateLabel} />
			</span>
		</>
	);

	const className =
		"grid min-h-12 grid-cols-[3px_1fr_auto] items-center gap-x-3 overflow-hidden rounded-[10px] border border-hairline bg-paper";

	const body = href ? (
		<a href={href} className={`${className} hover:border-line`}>
			{inner}
		</a>
	) : (
		<div className={className}>{inner}</div>
	);

	// The action sits outside the linked area so tapping it cannot navigate.
	if (!action) return body;
	return (
		<div className="flex items-stretch gap-1">
			<div className="min-w-0 flex-1">{body}</div>
			<div className="flex shrink-0 items-center">{action}</div>
		</div>
	);
}
